import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { api } from '../utils/api';

const EditFilingDialog = ({ filing, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    license_number: '',
    due_date: '',
    completed_date: '',
    owner: '',
    status: 'draft',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (filing) {
      setFormData({
        license_number: filing.license_number || '',
        due_date: filing.due_date ? new Date(filing.due_date).toISOString().split('T')[0] : '',
        completed_date: filing.completed_date ? new Date(filing.completed_date).toISOString().split('T')[0] : '',
        owner: filing.owner || '',
        status: filing.status || 'draft',
        notes: filing.notes || ''
      });
    }
  }, [filing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = {
        license_number: formData.license_number,
        owner: formData.owner,
        status: formData.status,
        notes: formData.notes
      };
      
      if (formData.due_date) {
        updates.due_date = new Date(formData.due_date).toISOString();
      }
      if (formData.completed_date) {
        updates.completed_date = new Date(formData.completed_date).toISOString();
      }

      await api.updateFiling(filing.filing_id, updates);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update filing:', error);
      alert('Failed to update filing');
    } finally {
      setLoading(false);
    }
  };

  if (!filing) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="edit-filing-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Filing</DialogTitle>
          <DialogDescription>
            Update filing information for {filing.filing_type} - {filing.state}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="license_number">License Number</Label>
              <Input
                id="license_number"
                value={formData.license_number}
                onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                placeholder="Associated license number (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="completed_date">Completed Date</Label>
                <Input
                  id="completed_date"
                  type="date"
                  value={formData.completed_date}
                  onChange={(e) => setFormData({ ...formData, completed_date: e.target.value })}
                  placeholder="Date completed (if applicable)"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="owner">Owner Email</Label>
              <Input
                id="owner"
                type="email"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="owner@losspay.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_preparation">In Preparation</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="deficient">Deficient</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add notes about this filing..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditFilingDialog;